import React from "react";

import { Box, Button, Circle, HStack, Link, Text } from "@chakra-ui/react";

import { ChevronLeftIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";

import { Permission } from "@/jenesys-types/jenesys-entities/permissions";

import { ConfirmModal } from "@/components/common/controls/ConfirmModal";
import { NamedLink } from "@/components/common/navigation/NamedLink";
import Barrier from "@/components/common/permission/Barrier";
import { AccountingConnections } from "@/components/features/settings/AccountingConnections";
import { ChartOfAccounts } from "@/components/features/settings/ChartOfAccounts";
import ConnectedBankAccounts from "@/components/features/settings/ConnectedBankAccounts";
import { IntegrateWhatsapp } from "@/components/features/settings/IntegrateWhatsapp";
import { Overview } from "@/components/features/settings/Overview";
import { ResendInviteModal } from "@/components/features/settings/ResendInviteModal";
import { TeamConnections } from "@/components/features/settings/TeamConnections";
import { useConnectionPlatform } from "@/hooks/features/useConnectionPlatform";

export interface ClientPractiseConnectionsDetailProps {}

export const ClientPractiseConnectionsDetail: React.FC<
  ClientPractiseConnectionsDetailProps
> = () => {
  const { category, clientId, platform: platformName } = useParams();
  const data = useConnectionPlatform({
    clientId,
    category,
    platformName
  });
  const {
    isComms,
    isEmail,
    helpLink,
    platform,
    isWhatsapp,
    isConnected,
    isBankRecon,
    isAccounting,
    selectedMember,
    whatsappStatus,
    whatsappDisclosure,
    handleResendInvite,
    isDisconnectingMember,
    isDisconnectingWhatsapp,
    isDisconnectingPendingWhatsapp,
    resendInviteDisclosure,
    disconnectMemberDisclosure,
    disconnectPendingInviteDisclosure,
    handleDisconnectMember,
    handleDisconnectWhatsapp,
    handleDisconnectPendingWhatsapp,
    isResendingInvite
  } = data;

  if (!platform) return null;

  return (
    <Box mt={8} rounded={16} px={16} py={16} bg="white">
      <Box>
        <Button
          as={NamedLink}
          name="settings.client.connections"
          size="sm"
          variant="ghost"
          leftIcon={<ChevronLeftIcon fontSize={24} />}
        >
          Connections
        </Button>
        {!isBankRecon && (
          <Overview {...data} platform={platform} category={category!} />
        )}
        {isBankRecon && <ConnectedBankAccounts {...data} platform={platform} />}
        {(isEmail || isComms) && (
          <HStack
            rounded={6}
            border="1px solid"
            borderColor="blackAlpha.300"
            mt={6}
            py={6}
            px={6}
            justify="space-between"
          >
            <Barrier
              show
              when={`company.connections.${category}` as Permission}
            >
              <Box>
                <Text fontSize={12} fontWeight={600}>
                  Status
                </Text>
                <HStack>
                  <Circle
                    size={2}
                    bg={isConnected ? "green.400" : "gray.500"}
                  />
                  <Text fontWeight={500}>
                    {isConnected ? "Connected" : "Disconnected"}
                  </Text>
                </HStack>
              </Box>
            </Barrier>

            <Box>
              <Text fontSize={12} fontWeight={600}>
                Help Docs
              </Text>
              <Box color="blue.700">
                <Link href={helpLink} target="_blank">
                  {helpLink}
                </Link>
              </Box>
            </Box>
          </HStack>
        )}

        {isAccounting && (
          <Barrier show when={`company.connections.${category}` as Permission}>
            <ChartOfAccounts {...data} />
          </Barrier>
        )}

        {(isEmail || isComms) && (
          <Barrier show when="company.members.read">
            <TeamConnections {...data} platform={platformName} />
          </Barrier>
        )}

        {isAccounting && (
          <Barrier show when="company.members.read">
            <AccountingConnections {...data} />
          </Barrier>
        )}
      </Box>

      {isComms && whatsappDisclosure.isOpen && (
        <IntegrateWhatsapp
          {...whatsappDisclosure}
          clientId={clientId}
          selectedMember={selectedMember}
          whatsappStatus={whatsappStatus}
        />
      )}

      {disconnectMemberDisclosure.isOpen && (
        <ConfirmModal
          {...disconnectMemberDisclosure}
          loadingText="Disconnecting..."
          isLoading={isDisconnectingMember || isDisconnectingWhatsapp}
          confirmText="Disconnect"
          title={`Disconnect ${platformName}`}
          content={`Are you sure want to permanently disconnect ${selectedMember?.user.first_name} ${selectedMember?.user.last_name} from ${platform.displayName}? They will no longer receive or send messages via our service. Are you sure you want to proceed?`}
          confirmAction={
            isWhatsapp ? handleDisconnectWhatsapp : handleDisconnectMember
          }
        />
      )}

      {disconnectPendingInviteDisclosure.isOpen && (
        <ConfirmModal
          {...disconnectPendingInviteDisclosure}
          loadingText="Canceling invitation..."
          isLoading={isDisconnectingPendingWhatsapp}
          confirmText="Cancel Invitation"
          title={`Cancel Pending ${platformName} Invitation`}
          content={`Are you sure you want to cancel the pending ${platform.displayName} invitation for ${selectedMember?.user.first_name} ${selectedMember?.user.last_name}? This will cancel their pending invitation and they will no longer be able to connect using the current invitation link.`}
          confirmAction={handleDisconnectPendingWhatsapp}
        />
      )}

      {resendInviteDisclosure.isOpen && selectedMember && (
        <ResendInviteModal
          {...resendInviteDisclosure}
          isLoading={isResendingInvite}
          handleResendInvite={handleResendInvite}
          selectedMember={selectedMember}
        />
      )}
    </Box>
  );
};
